import { Helmet } from "react-helmet";
import { getCanonicalUrl } from "../helpers/canonicalUrl";
import React from "react";
import { Container, ContentWithPaddingXl } from "components/misc/Layouts";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro";
import Header from "components/headers/light.js";
import Footer from "components/footers/FiveColumnWithInputForm.js";
import { SectionHeading } from "components/misc/Headings";

import list from "../conferences.json";
import { StyledLayout } from "../helpers/AnimationRevealPage";

const HeadingRow = tw.div`flex flex-col items-center text-center mb-12`;
const Heading = tw(SectionHeading)`text-gray-900 text-4xl md:text-5xl font-bold mb-4`;
const Posts = tw.div`grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 mt-12`;

const SubHeading = tw.h2`text-xl md:text-2xl lg:text-3xl font-semibold text-gray-700 mb-4 max-w-4xl`;
const SubheadingDesc = tw.h3`text-base md:text-lg text-gray-600 max-w-3xl leading-relaxed`;

const PostContainer = styled.div`
  ${tw`w-full`}
`;

const Post = tw.div`cursor-pointer flex flex-col bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 h-full overflow-hidden border border-gray-100`;

const ImageContainer = styled.div`
  ${tw`relative overflow-hidden bg-gray-100 p-6`}
`;

const Image = styled.div`
  ${(props) =>
    css`
      background-image: url("${props.image}");
    `}
  ${tw`h-48 md:h-56 lg:h-64 w-full bg-contain bg-no-repeat bg-center rounded-xl`}
`;

const Info = tw.div`p-6 md:p-8 flex flex-col`;
const Category = tw.div`uppercase text-primary-600 text-xs font-bold tracking-widest mb-3 bg-gray-100 px-3 py-1 rounded-full inline-block`;
const Title = tw.div`font-bold mb-4 text-lg md:text-xl lg:text-2xl text-gray-900 group-hover:text-primary-600 transition duration-300 leading-tight flex-1`;
const Description = tw.div`text-gray-600 text-sm md:text-base leading-relaxed mt-auto`;

export default () => {
  const papers = [...list.sort((a, b) => new Date(b.date) - new Date(a.date))];

  return (
    <>
      <Helmet>
        <title>Conferences & Events - MAI Motion Presentations & Healthcare Innovation</title>
        <meta name="description" content="Discover MAI Motion and onMRI research presentations at leading medical conferences. MSK Doctors presents cutting-edge AI motion analysis and MRI technology at European and international healthcare events." />
        <meta name="keywords" content="MAI Motion conferences, medical conferences, healthcare innovation, AI motion analysis, onMRI presentations, MSK Doctors research, biomechanics conferences, orthopedic conferences, European medical symposium, British Orthopaedic Association, United European Academy, musculoskeletal research presentations" />
        <link rel="canonical" href={getCanonicalUrl("/conferences")} />
        <meta property="og:title" content="Conferences & Events - MAI Motion Presentations & Healthcare Innovation" />
        <meta property="og:description" content="Discover MAI Motion and onMRI research presentations at leading medical conferences. MSK Doctors presents cutting-edge AI motion analysis and MRI technology." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://maimotion.com/conferences" />
        <meta property="og:image" content="https://d3ekn1do43nbxb.cloudfront.net/logo/mai-logo.png" />
        <meta name="twitter:title" content="Conferences & Events - MAI Motion Presentations" />
        <meta name="twitter:description" content="Discover MAI Motion and onMRI research presentations at leading medical conferences." />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="robots" content="index,follow" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "Conferences & Events",
            "description": "MAI Motion and onMRI research presentations at leading medical conferences",
            "url": "https://maimotion.com/conferences",
            "mainEntity": {
              "@type": "ItemList",
              "numberOfItems": papers.length,
              "itemListElement": papers.map((paper, index) => ({
                "@type": "ListItem",
                "position": index + 1,
                "item": {
                  "@type": "Article",
                  "name": paper.title,
                  "description": paper.shortDescription,
                  "url": `https://maimotion.com/conferences/${paper.slug}`,
                  "image": paper.image,
                  "datePublished": paper.date
                }
              }))
            },
            "publisher": {
              "@type": "Organization",
              "name": "MAI Motion",
              "logo": {
                "@type": "ImageObject",
                "url": "https://maimotion.com/logo.png"
              }
            }
          })}
        </script>
      </Helmet>
      <StyledLayout className="App" style={{ padding: '1rem' }}>
      <Header />
      <Container>
        <ContentWithPaddingXl>
          <HeadingRow>
            <Heading>Conferences</Heading>
            <SubHeading>
              Advancing musculoskeletal health through MAI Motion and OnMRI
            </SubHeading>
            <SubheadingDesc>
              MSK Doctors presented original research at national and European
              meetings. Below is a summary of our recent conference activity.
            </SubheadingDesc>
          </HeadingRow>
          <Posts>
            {papers.map((post, index) => (
              <PostContainer key={index}>
                <Post
                  className="group"
                  as="a"
                  href={`/conferences/${post.slug}`}
                >
                  <ImageContainer>
                    <Image image={post.image} />
                  </ImageContainer>
                  <Info>
                    {post.category && <Category>{post.category}</Category>}
                    <Title>{post.title}</Title>
                    {post.shortDescription && (
                      <Description>{post.shortDescription}</Description>
                    )}
                  </Info>
                </Post>
              </PostContainer>
            ))}
          </Posts>
        </ContentWithPaddingXl>
      </Container>
      <Footer />
      </StyledLayout>
    </>
  );
};
